/**
 * NotificationsSheet Component
 *
 * BottomSheet overlay showing operator notifications.
 * Marks all as read on open (fire-and-forget via mutate()).
 * Lists notification cards with type icon, title, message,
 * relative timestamp, and read/unread indicator.
 *
 * Mobile-first: touch targets 44px+, notification cards 64px+.
 * Uses active: instead of hover: for touch devices.
 */

import { useEffect } from 'react';
import {
  Bell,
  BookOpen,
  Clock,
  AlertTriangle,
  ClipboardCheck,
  TrendingDown,
  CheckCircle,
  XCircle,
  Megaphone,
  type LucideIcon,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { BottomSheet } from './BottomSheet';
import {
  useOperatorNotifications,
  useOperatorMarkAllAsRead,
} from '../hooks/useOperatorNotifications';
import type { Notification, NotificationType } from '../../../hooks/useNotifications';
import { cn } from '../../../lib/utils';

interface NotificationsSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

// Notification type -> icon + color mapping (Lucide icons for mobile clarity)
const typeConfig: Record<
  NotificationType,
  { icon: LucideIcon; colorClass: string; bgClass: string }
> = {
  training_assigned: {
    icon: BookOpen,
    colorClass: 'text-blue-600',
    bgClass: 'bg-blue-100',
  },
  training_reminder: {
    icon: Clock,
    colorClass: 'text-amber-600',
    bgClass: 'bg-amber-100',
  },
  training_overdue: {
    icon: AlertTriangle,
    colorClass: 'text-red-600',
    bgClass: 'bg-red-100',
  },
  assessment_created: {
    icon: ClipboardCheck,
    colorClass: 'text-green-600',
    bgClass: 'bg-green-100',
  },
  assessment_score_low: {
    icon: TrendingDown,
    colorClass: 'text-red-600',
    bgClass: 'bg-red-100',
  },
  sop_processing_complete: {
    icon: CheckCircle,
    colorClass: 'text-green-600',
    bgClass: 'bg-green-100',
  },
  sop_processing_failed: {
    icon: XCircle,
    colorClass: 'text-red-600',
    bgClass: 'bg-red-100',
  },
  system_announcement: {
    icon: Megaphone,
    colorClass: 'text-purple-600',
    bgClass: 'bg-purple-100',
  },
};

function NotificationCard({ notification }: { notification: Notification }) {
  const config = typeConfig[notification.type] ?? {
    icon: Bell,
    colorClass: 'text-gray-600',
    bgClass: 'bg-gray-100',
  };
  const Icon = config.icon;

  const timeAgo = formatDistanceToNow(new Date(notification.createdAt), {
    addSuffix: true,
  });

  return (
    <div
      className={cn(
        'relative flex gap-3 px-4 py-3 transition-colors duration-150',
        !notification.isRead && 'bg-blue-50/60'
      )}
    >
      {/* Unread indicator: left blue bar */}
      {!notification.isRead && (
        <div className="absolute left-0 top-2 bottom-2 w-1 rounded-r-full bg-blue-500" />
      )}

      {/* Type icon */}
      <div
        className={cn(
          'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full',
          config.bgClass
        )}
      >
        <Icon size={20} strokeWidth={1.5} className={config.colorClass} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            'text-sm text-op-text-primary',
            !notification.isRead ? 'font-semibold' : 'font-medium'
          )}
        >
          {notification.title}
        </p>
        <p className="mt-0.5 text-sm text-op-text-secondary line-clamp-2">
          {notification.message}
        </p>
        <p className="mt-1 text-xs text-op-text-tertiary">{timeAgo}</p>
      </div>
    </div>
  );
}

export function NotificationsSheet({ isOpen, onClose }: NotificationsSheetProps) {
  const { data: notificationsData, isLoading } = useOperatorNotifications({
    limit: 30,
  });
  const markAllAsRead = useOperatorMarkAllAsRead();

  const notifications = notificationsData?.data ?? [];

  // Mark all as read on open (fire-and-forget -- silent on failure)
  useEffect(() => {
    if (isOpen) {
      markAllAsRead.mutate(); // NOT mutateAsync -- silent on failure
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="Notifications" snapTo="full">
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-guild-green border-t-transparent" />
        </div>
      ) : notifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-op-text-tertiary">
          <Bell size={40} strokeWidth={1} className="mb-3 text-gray-300" />
          <p className="text-sm font-medium">No notifications yet</p>
          <p className="mt-1 text-xs">
            You'll see training assignments and assessments here
          </p>
        </div>
      ) : (
        <div className="-mx-4 divide-y divide-op-muted">
          {notifications.map((notification) => (
            <NotificationCard key={notification.id} notification={notification} />
          ))}
        </div>
      )}
    </BottomSheet>
  );
}
